import React, { useEffect } from 'react';
import { OptionItemProps } from 'src/components/ui/OptionItem/OptionItem';
import { useEventListeners, useOutsideClick } from 'src/hooks/utils';
import Portal from 'src/components/layouts/Portal/Portal';
import ActionMenu from 'src/components/layouts/ActionMenu/ActionMenu';
import Icon from 'src/components/ui/Icon/Icon';

export interface ActionMenuItemProps {
  menuItems: OptionItemProps[];
}

const ActionMenuButton: React.FC<ActionMenuItemProps> = ({ menuItems }) => {
  const rowRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [menuVisible, setMenuVisible] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState({ top: 0, right: 0 });

  useOutsideClick(
    menuRef,
    e => {
      if (!rowRef.current?.contains(e.target as Node)) {
        setMenuVisible(false);
      }
    },
    // Capture phase: the React Flow canvas stops propagation of its pointer
    // events, so a bubble-phase listener never fires when clicking the canvas.
    true,
  );

  useEventListeners([{ event: 'wheel' }], () => setMenuVisible(false));

  const handleResize = () => {
    const rowRect = rowRef.current?.getBoundingClientRect();
    const rowTop = rowRect?.top;
    const rowLeft = rowRect?.left;

    if (rowTop && rowLeft) {
      setMenuPosition({ top: rowTop, right: window.innerWidth - rowLeft });
    }
  };

  useEventListeners([{ event: 'resize' }], handleResize);

  useEffect(() => {
    handleResize();
  }, [rowRef.current]);

  useEffect(() => {
    function handleTouch(e: TouchEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuVisible(false);
      }
    }
    document.addEventListener('touchstart', handleTouch);
    return () => {
      document.removeEventListener('touchstart', handleTouch);
    };
  }, []);

  return (
    <>
      <button
        aria-haspopup="true"
        aria-label="Actions"
        type="button"
        className="cursor-pointer"
        onClick={event => {
          event.stopPropagation();
          handleResize();
          setMenuVisible(!menuVisible);
        }}
        ref={rowRef}>
        <Icon className="text-full opacity-60 flex justify-center" name="settings" />
      </button>
      {menuVisible && (
        <Portal position={menuPosition}>
          <div ref={menuRef}>
            <ActionMenu items={menuItems} />
          </div>
        </Portal>
      )}
    </>
  );
};

export default ActionMenuButton;
